import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";

const Breadcrumbs = () => {
  const location = useLocation();
  const params = useParams();
  const pathnames = location.pathname.split("/").filter((x) => x);

  // Dashboard par hide rakhein
  if (location.pathname === "/dashboard") return null;

  // `params.id` is the PATIENT id ONLY on the /patients/manage/:id/... routes.
  // On every other route (form add/edit where :id is the RECORD id, inquiry/:id, etc.)
  // it is NOT a patient id — treating it as one writes a record id into
  // sessionStorage.lastPatientId and links the crumbs to the wrong patient.
  const onPatientManageRoute = pathnames.includes("patients") && pathnames.includes("manage");
  const routePatientId = onPatientManageRoute ? params.id : undefined;

  // Data from state or params for logical links
  let caseId = params.caseId || location.state?.caseId;
  let patientId = routePatientId || location.state?.patientId;
  const activeFormName = location.state?.formName;

  // When a patient route param (:id) is present we are at patient level, ABOVE any
  // specific case (case routes never carry :id). Never fall back to a stale case from
  // a previously-viewed patient here — clear it so no wrong "Registration Profile" crumb shows.
  const atPatientLevel = !!routePatientId && !params.caseId && !location.state?.caseId;

  if (caseId) {
    sessionStorage.setItem("lastCaseId", caseId);
  } else if (atPatientLevel) {
    sessionStorage.removeItem("lastCaseId");
    caseId = null;
  } else {
    caseId = sessionStorage.getItem("lastCaseId");
  }

  if (patientId) {
    sessionStorage.setItem("lastPatientId", patientId);
  } else {
    patientId = sessionStorage.getItem("lastPatientId");
  }

  // 🧹 Filter segments like IDs and Phases
  const shouldSkip = (segment) => {
    const isId = /^\d+$/.test(segment) || /^[0-9a-fA-F-]{8,}$/.test(segment);
    const isPhase = /^phase-?\d*$/i.test(segment);
    const isRedundant = ["manage", "profile", "cases"].includes(segment.toLowerCase());
    return isId || isPhase || isRedundant;
  };

  const nameMap = {
    patients: "Active Clients",
    inquiry: "Inquiry",
    consent: "Consent Form",
    "nursing-records": "Nursing Record",
    "psychiatric-order": "Psychiatric Order",
    "adl-record-sheet": "ADL Record",
    "nursing-opd-assessments": "Nursing OPD Assessments",
    "doctor-opd-assessments": "Doctor OPD Assessments",
    "reports": "Reports",
    "general": "General Reports",
    "tracking-metrics": "Tracking Metrics",
    "home-visits": "Home Visits",
    "opds": "OPD Reports",
  };

  const getDisplayName = (name) => nameMap[name] || name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, " ");

  const buildCrumbs = () => {
    let crumbs = [{ name: "Dashboard", path: "/dashboard" }];

    // 1. Sidebar Direct Links Logic (Inquiries & Cases List)
    // Agar hum main list par hain toh wahi dikhayen, Patients beech mein na aaye
    if (pathnames.includes("inquiries")) {
      crumbs.push({ name: "Inquiries", path: null });
      return crumbs;
    }

    // Sidebar se jab "Cases" par click ho (URL: /cases)
    if (pathnames.length === 1 && pathnames[0] === "cases") {
      crumbs.push({ name: "Registrations", path: null });
      return crumbs;
    }

    // 2. Patient & Case Profile Logic (Nested Workflow)
    // Patients link tabhi dikhayen jab URL mein 'patients' ho ya patientId ho
    if (pathnames.includes("patients") || patientId) {
      crumbs.push({ name: "Active Clients", path: "/patients" });
    }

    // Patient Profile (Manage page)
    if (pathnames.includes("manage") && patientId) {
      crumbs.push({ name: "Active Client Profile", path: `/patients/manage/${patientId}/cases` });
    }

    // Case Profile tabhi add karein jab hum waqai kisi case ke andar hon
    if (caseId) {
      crumbs.push({ name: "Registration Profile", path: `/cases/${caseId}/profile` });
    }

    // 3. Form Action / Last Segment Logic
    const lastPart = pathnames[pathnames.length - 1];
    const isAction = ["add", "edit", "print"].includes(lastPart.toLowerCase());

    if (activeFormName) {
      const prefix = isAction ? lastPart.charAt(0).toUpperCase() + lastPart.slice(1) : "";
      crumbs.push({ name: `${prefix} ${activeFormName}`.trim(), path: null });
    } else {
      // General fallback loop
      pathnames.forEach((name) => {
        const displayName = getDisplayName(name);
        // Skip filtering redundant segments and already added ones
        if (!shouldSkip(name) && !crumbs.find(c => c.name === displayName)) {
          crumbs.push({ name: displayName, path: null });
        }
      });
    }

    return crumbs;
  };

  const finalCrumbs = buildCrumbs();

  return (
    <div className="layout-wrapper mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
      <nav aria-label="breadcrumb" className="mb-0">
        <ol className="breadcrumb bg-light p-2 rounded shadow-sm border-0 mb-0">
          {finalCrumbs.map((crumb, index) => {
            const isLast = index === finalCrumbs.length - 1;
            // Form Name hamesha last segment hoga, isliye non-clickable rahega
            const isClickable = !isLast && crumb.path;

            return (
              <li key={index} className={`breadcrumb-item ${isLast ? "active" : ""}`} aria-current={isLast ? "page" : undefined}>
                {isClickable ? (
                  <Link to={crumb.path} className="text-decoration-none text-primary">
                    {crumb.name === "Dashboard" && <i className="ri-home-4-line me-1"></i>}
                    {crumb.name}
                  </Link>
                ) : (
                  <span className={isLast ? "text-dark fw-bold" : "text-secondary"}>
                    {crumb.name === "Dashboard" && <i className="ri-home-4-line me-1"></i>}
                    {crumb.name}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
      {caseId && (pathnames.includes("add") || pathnames.includes("edit") || pathnames.includes("print")) && !location.pathname.endsWith("/profile") && (
        <Link
          to={`/cases/${caseId}/profile`}
          state={{ caseId, patientId }}
          className="btn btn-outline-secondary btn-sm"
          style={{ textDecoration: "none" }}
        >
          <i className="ri-arrow-left-line me-1"></i> Back to Registration Profile
        </Link>
      )}
    </div>
  );
};

export default Breadcrumbs;